import {colors} from '@commercial-helios/ui'
import {tokens} from '@pleo-io/telescope'
import React from 'react'
import styled, {keyframes} from 'styled-components'

import Button from '@helios/shared/components/button'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

const bgColors = {
    white: colors.background.default,
    grey: colors.background.darkGrey,
    pink: colors.background.pink,
}

type BgColor = keyof typeof bgColors
type SizeVariant = 'small' | 'large'

export const isAllowedBgColor = (color: string): color is BgColor =>
    Object.keys(bgColors).includes(color)

export const isAllowedSizeVariant = (sizeVariant: string): sizeVariant is SizeVariant =>
    ['small', 'large'].includes(sizeVariant)

export interface Props {
    items: string[]
    ctaText?: string
    ctaLink?: PrismicLinkProps
    sizeVariant?: SizeVariant
    bgColor?: BgColor
}

export const RotatingBanner = ({
    items,
    ctaText,
    ctaLink,
    sizeVariant = 'small',
    bgColor = 'pink',
}: Props) => {
    const itemsGroup = (
        <ItemsGroup>
            {items.map((item, index) => (
                <li key={index}>
                    <Text
                        variant={sizeVariant === 'small' ? 'paragraph-large' : 'heading-base'}
                        color={bgColor === 'grey' ? 'inverted' : 'default'}
                    >
                        {item}
                    </Text>
                </li>
            ))}

            {ctaLink && ctaText && sizeVariant === 'large' && (
                <li>
                    <StyledButton
                        forwardedAs={PrismicLink}
                        variant={bgColor === 'grey' ? 'inverted' : 'default'}
                        {...ctaLink}
                    >
                        {ctaText}
                    </StyledButton>
                </li>
            )}
        </ItemsGroup>
    )

    // We render a 2nd chunk of items so they can slide into view as the first chunk slides out of view
    return (
        <Wrapper role="marquee" $bgColor={bgColor}>
            {itemsGroup}
            {itemsGroup}
        </Wrapper>
    )
}

const itemSpacing = tokens.spacing56
const mobileBreakpoint = 'md'

type WrapperProps = {
    $bgColor: BgColor
}

const slide = keyframes`
    0% {
        transform: translateX(${itemSpacing});
    }

    100% {
        transform: translateX(-100%);
    }
`

const Wrapper = styled.div<WrapperProps>`
    display: grid;
    width: 200%;
    padding: ${tokens.spacing12} 0;
    background-color: ${({$bgColor}) => bgColors[$bgColor]};
    grid-auto-flow: column;
    column-gap: ${itemSpacing};
`

const ItemsGroup = styled.ul`
    display: grid;
    padding: 0;
    margin: 0;
    white-space: nowrap;
    list-style-type: none;
    animation: ${slide} 20s linear infinite;
    grid-auto-flow: column;
    align-items: center;
    justify-items: center;
    column-gap: ${itemSpacing};

    .no-touch ${Wrapper}:hover & {
        animation-play-state: paused;
    }
`

const StyledButton = styled(Button)`
    ${mq.max(mobileBreakpoint)} {
        display: none;
    }
`
